/**
 * Member cards-on-file API wrapper (DNKADM-0252/DNKADM-0262 — consumes the
 * dink-api handoff of 2026-08-04/05). The member's saved cards: list, save one
 * taken over the counter, change the billing card additively, replace one that bills
 * subscriptions, and remove one.
 * Built against **dink-api only** — this app never calls pay-api and never sees a
 * PSP identifier.
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the save/remove writes.
 *
 * **Endpoints:**
 *  - `GET    /v2/admin/members/{contactId}/cards`            → `{ cards: MemberCard[] }`
 *    (tolerates a bare array). An empty list is a `200` with an empty array, never a `404`.
 *  - `POST   /v2/admin/members/{contactId}/cards`            ← `{ cardToken, locationId? }`
 *    → the saved card plus `reused`.
 *  - `PUT    /v2/admin/memberships/{contactId}/subscription/payment-method`
 *    ← `{ cardId, idempotencyKey } | { cardToken, idempotencyKey }`
 *    → the updated billing card. The old card stays on file.
 *  - `POST   /v2/admin/members/{contactId}/cards/{cardId}/replace`
 *    ← `{ cardToken, idempotencyKey } | { replacementCardId, idempotencyKey }`
 *    → the replacement card plus moved subscription ids. The response is the
 *    authoritative post-state; callers render it and do not re-fetch.
 *  - `DELETE /v2/admin/members/{contactId}/cards/{cardId}`   → `204 | 404 | 409 | 502`
 *    (the `ApiError` propagates so the caller can branch on status/code).
 *
 * **The rules this module exists to hold:**
 *  - **`cardId` is OPAQUE.** It is never parsed for meaning and never displayed. Display
 *    uses `maskedPan` / `cardBrand` / `expMonth` / `expYear`.
 *  - **Saving moves NO money** — no charge, no authorization hold, nothing on the
 *    member's statement. It stores a card for a later sale.
 *  - **Never cache the list.** The caller refetches after every save, billing-card change,
 *    and every remove; a
 *    stale list means an admin charging a card that no longer exists. Replace is the
 *    deliberate exception: its `200` body is the authoritative post-state, so the caller
 *    renders that body instead of re-reading.
 *  - **No PAN or CVV reaches any server.** The browser tokenizes (Square Web Payments
 *    SDK) and only the single-use nonce (`cardToken`) moves; it is never stored.
 *  - **Expired cards are NOT hidden** by the server — expiry is flagged client-side from
 *    `expMonth`/`expYear` so an admin can clean them up.
 *
 * Every read is parsed DEFENSIVELY (mirrors `creditBuckets.ts`): a row without a usable
 * `cardId` is **dropped** rather than surfaced half-identified, every other field
 * degrades to `null`, and server order is preserved (newest first — never re-sorted).
 */
import { request } from './client';
import { ApiError } from './errors';
import { v2ErrorCode } from './v2Errors';

/** v2 admin members base path (bearer surface); cards hang off one member. */
const PATH = '/v2/admin/members';
const MEMBERSHIPS_PATH = '/v2/admin/memberships';

const changeBillingPaymentMethodFields = {
  savedCardId: 'cardId',
  cardToken: 'cardToken',
  idempotencyKey: 'idempotencyKey',
} as const;

function cardsPath(contactId: string): string {
  return `${PATH}/${encodeURIComponent(contactId)}/cards`;
}

/** Assumed DNKADM-0262 replace route: one suffix under the shipped member-card base. */
function replaceCardPath(contactId: string, cardId: string): string {
  return `${cardsPath(contactId)}/${encodeURIComponent(cardId)}/replace`;
}

/** Pending DNKAPI-0428 admin swap route; keep isolated for the forward-contract rename. */
function changeBillingPaymentMethodPath(contactId: string): string {
  return `${MEMBERSHIPS_PATH}/${encodeURIComponent(contactId)}/subscription/payment-method`;
}

/**
 * One card a member has on file. `cardId` is **OPAQUE** — the delete target only,
 * never parsed and never displayed. Every display field is nullable: the UI degrades a
 * missing one to its placeholder rather than blanking the row.
 */
export interface MemberCard {
  cardId: string;
  maskedPan: string | null;
  cardBrand: string | null;
  expMonth: number | null;
  expYear: number | null;
  createdAt: string | null;
  /**
   * Live subscription ids this card pays for. **Absent (null) when the card bills
   * nothing** — pay-api never sends `[]`, so PRESENCE is the signal, not length.
   * A card that bills something cannot be removed (409 CARD_IN_USE); it is replaced.
   * Ids only — pay-api will not send plan names; resolve them locally.
   */
  billsSubscriptions: string[] | null;
}

/** The save response: the saved card plus whether it was already on file. */
export interface SavedMemberCard extends MemberCard {
  /** `true` = the card was already on file. A SUCCESS, never an error. */
  reused: boolean;
}

/**
 * Exactly one of `cardToken` (a fresh nonce) or `replacementCardId` (another saved card).
 * `replacementCardId` is deliberately NOT named `cardId` — the path already carries a card
 * id, and one request naming two different cards under one key is how the wrong card gets
 * retired.
 */
type ReplaceCredential =
  | { cardToken: string; replacementCardId?: never }
  | { replacementCardId: string; cardToken?: never };

/** The replace request. `idempotencyKey` is REQUIRED and is re-sent on transient retry. */
export type ReplaceCardInput = ReplaceCredential & { idempotencyKey: string };

/**
 * Exactly one credential for changing billing. `cardId` is a saved card; `cardToken`
 * is a fresh browser-tokenized nonce. The old billing card is not named here or in the
 * path because Make default is additive: it stays on file.
 */
type ChangeBillingCredential =
  | { cardId: string; cardToken?: never }
  | { cardToken: string; cardId?: never };

/**
 * The change request names exactly one target credential. Callers may provide an
 * idempotency key for same-body retries; otherwise this helper mints one before
 * sending the shipped wire contract.
 */
export type ChangeMemberCardBillingInput = ChangeBillingCredential & { idempotencyKey?: string };

export interface ReplaceCardResult {
  /** The replacement card that remains on file after the old card is retired. */
  card: MemberCard | null;
  /** Subscription ids dink-api reports as moved; absent/unknown parses to an empty list. */
  movedSubscriptionIds: string[];
}

export type MemberCardTransientRetry =
  | 'processor-unavailable'
  | 'card-retiring'
  | 'idempotency-in-progress';

// --- Defensive parsing helpers (mirrors creditBuckets.ts) ----------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064 wire discipline). The
 * card id is opaque either way — this only normalizes the wire type so a numeric id
 * is never dropped.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A list of usable string ids; a non-array or empty array normalizes to null. */
function asNullableStringIds(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const ids = value
    .filter((entry): entry is string => {
      return typeof entry === 'string' && entry.trim().length > 0;
    })
    .map((entry) => entry.trim());
  return ids.length > 0 ? ids : null;
}

/** A list of moved ids; absent/unknown is an empty list on replace result metadata. */
function asStringIds(value: unknown): string[] {
  return asNullableStringIds(value) ?? [];
}

/** A finite number (number or numeric string), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

/** The nullable display fields shared by a listed card and a saved one. */
function parseCardFields(raw: Record<string, unknown>): Omit<MemberCard, 'cardId'> {
  return {
    maskedPan: asText(raw['maskedPan']),
    cardBrand: asText(raw['cardBrand']),
    // Kept as the server states them — expiry is INFERRED from these client-side, so a
    // missing one must stay `null` (absence is never evidence of expiry).
    expMonth: asNumber(raw['expMonth']),
    expYear: asNumber(raw['expYear']),
    createdAt: asText(raw['createdAt']),
    billsSubscriptions: asNullableStringIds(raw['billsSubscriptions']),
  };
}

/**
 * Parse one listed card, or `null` when it carries no usable `cardId` — a card that
 * cannot be identified cannot be removed either, so it is DROPPED rather than shown
 * half-identified (mirrors `creditBuckets.ts`'s `parseBucket`).
 */
function parseCard(raw: unknown): MemberCard | null {
  if (!isObject(raw)) return null;
  const cardId = asId(raw['cardId']);
  if (cardId === null) return null;
  return { cardId, ...parseCardFields(raw) };
}

/**
 * Extract the cards array TOLERANTLY: the documented `{ cards: [...] }` envelope or a
 * bare array. Anything else yields `null`, which the list read turns into `[]` (an
 * empty list is a real state — a member with no cards on file).
 */
function extractCards(body: unknown): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (isObject(body) && Array.isArray(body['cards'])) return body['cards'];
  return null;
}

/**
 * Parse the save response. The write already SUCCEEDED by the time this runs, so it
 * never rejects a body: `reused` defaults to `false` and an unreadable `cardId`
 * degrades to `''` rather than throwing away a real save. The caller does not act on
 * the returned id — the mandatory list refetch is the authoritative view of what is
 * now on file.
 */
function parseSavedCard(body: unknown): SavedMemberCard {
  const raw = isObject(body) ? body : {};
  return {
    cardId: asId(raw['cardId']) ?? '',
    ...parseCardFields(raw),
    reused: raw['reused'] === true,
  };
}

function parseReplaceCardResult(body: unknown): ReplaceCardResult {
  const raw = isObject(body) ? body : {};
  const rawCard =
    raw['card'] ?? raw['newCard'] ?? raw['replacementCard'] ?? raw['replacement'] ?? body;
  return {
    card: parseCard(rawCard),
    movedSubscriptionIds: asStringIds(raw['movedSubscriptionIds'] ?? raw['movedSubscriptions']),
  };
}

function parseChangedBillingCard(body: unknown): MemberCard | null {
  const raw = isObject(body) ? body : {};
  return parseCard(raw['card'] ?? raw['billingCard'] ?? raw['paymentMethod'] ?? body);
}

/**
 * Shared transient card mutation classification. `CARD_RETIRING` and
 * `IDEMPOTENCY_IN_PROGRESS` mean a wallet mutation is still settling; callers retry
 * with the same logical operation and idempotency key.
 */
export function memberCardTransientRetry(error: unknown): MemberCardTransientRetry | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 502) return 'processor-unavailable';
  if (error.status === 409 && v2ErrorCode(error) === 'CARD_RETIRING') return 'card-retiring';
  if (error.status === 409 && v2ErrorCode(error) === 'IDEMPOTENCY_IN_PROGRESS') {
    return 'idempotency-in-progress';
  }
  return null;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/members/{contactId}/cards` → the member's cards on file, in the
 * SERVER's order (newest first — never re-sorted here). A row with no usable `cardId`
 * is dropped; a body that yields no array degrades to `[]`. Throws a typed `ApiError`
 * on a non-2xx response.
 *
 * The result must NOT be cached — refetch after every save and every remove.
 */
export async function listMemberCards(contactId: string): Promise<MemberCard[]> {
  const body = await request<unknown>('GET', cardsPath(contactId));
  return (extractCards(body) ?? [])
    .map(parseCard)
    .filter((card): card is MemberCard => card !== null);
}

/**
 * `POST /v2/admin/members/{contactId}/cards` → save a card from a browser-tokenized
 * nonce. **Saving moves no money.** Returns the saved card; `reused: true` means it was
 * already on file, which is a SUCCESS.
 *
 * `locationId` **BINDS** the card to that location's payment connection — always send
 * the location the admin is working at. Omitting it binds the card to the member's HOME
 * location, which is exactly what later produces a `422 SAVED_CARD_CONNECTION_MISMATCH`
 * on a membership sale at a different site. The key is included only when the value is
 * a finite number and is otherwise **omitted entirely** — an explicit `null`/`NaN` would
 * read as a deliberate unbound rather than a defaulted one.
 *
 * The `cardToken` is a single-use nonce: it is sent and never retained.
 */
export async function saveMemberCard(
  contactId: string,
  cardToken: string,
  locationId?: number,
): Promise<SavedMemberCard> {
  const json: Record<string, unknown> = { cardToken };
  if (typeof locationId === 'number' && Number.isFinite(locationId)) {
    json['locationId'] = locationId;
  }
  const body = await request<unknown>('POST', cardsPath(contactId), {
    json,
  });
  return parseSavedCard(body);
}

/**
 * `POST /v2/admin/members/{contactId}/cards/{cardId}/replace` — the new card takes over
 * everything the old one billed, and the OLD CARD IS GONE afterwards. The `200` body is
 * the authoritative post-state: render it, never re-fetch.
 *
 * Send only the replacement credential plus `idempotencyKey`; dink-api derives member/contact
 * context from the path just as the shipped save call derives it from this surface.
 */
export async function replaceMemberCard(
  contactId: string,
  cardId: string,
  input: ReplaceCardInput,
): Promise<ReplaceCardResult> {
  const body = await request<unknown>('POST', replaceCardPath(contactId, cardId), {
    json: input,
  });
  return parseReplaceCardResult(body);
}

/**
 * `PUT /v2/admin/memberships/{contactId}/subscription/payment-method` — changes the
 * membership billing card to another saved card or a fresh tokenized card. This is
 * additive: the old billing card stays on file, and callers refetch the wallet after a
 * success so the old card becomes remove-eligible when the server reports it.
 */
export async function changeMemberCardBilling(
  contactId: string,
  input: ChangeMemberCardBillingInput,
): Promise<MemberCard | null> {
  const json: Record<string, unknown> = {
    [changeBillingPaymentMethodFields.idempotencyKey]: input.idempotencyKey ?? crypto.randomUUID(),
  };
  if (typeof input.cardId === 'string') {
    json[changeBillingPaymentMethodFields.savedCardId] = input.cardId;
  } else {
    json[changeBillingPaymentMethodFields.cardToken] = input.cardToken;
  }
  const body = await request<unknown>('PUT', changeBillingPaymentMethodPath(contactId), { json });
  return parseChangedBillingCard(body);
}

/**
 * `DELETE /v2/admin/members/{contactId}/cards/{cardId}` → remove a card on file.
 * Resolves on `204`. The typed `ApiError` PROPAGATES so the caller can branch on the
 * documented outcomes: `404` (already gone / not this member's), `409 CARD_IN_USE` (a
 * live membership bills this card — its `message` is shown verbatim), `502` (the card
 * processor is unreachable; nothing was removed).
 */
export async function removeMemberCard(contactId: string, cardId: string): Promise<void> {
  await request<unknown>('DELETE', `${cardsPath(contactId)}/${encodeURIComponent(cardId)}`);
}
